<template>
  <div :style="`height: ${height}px;overflow-y: scroll; ${backgroundStyle}`">
    <div class="w-100 p-3 d-flex align-items-center justify-content-between" style="background-color: rgba(250, 250, 250, 0.7);">
      <span>
        <strong>{{ $t("js.backend.ballot_preview.interactive_preview", initialLocale) }}</strong>
        <i class="fas fa-info-circle text-dark ms-1 cursor-pointer" @click="toggleModal"></i>
      </span>

      <div class="d-flex gap-2">
        <button
          :class="`btn btn-sm ${contestsOpened ? 'btn-danger' : 'btn-success'}`"
          @click="toggleContestsOpened"
          v-text="contestsOpened ? $t('js.backend.ballot_preview.close_ballot') : $t('js.backend.ballot_preview.open_ballot')"
        />
        <select class="btn btn-sm btn-dark dropdown-toggle" @change="setSelectedLocale($event.target.value)">
          <option v-for="locale in availableLocales" :key="`${locale}-locale`" :value="locale">{{ $t('js.language_name', locale) }}</option>
        </select>
      </div>
    </div>

    <div class="p-0 m-0">
      <div class="container p-0 px-md-5 pt-sm-3 pt-md-5 pb-5">
        <div class="card border-0 rounded-0 shadow-lg h-100 mb-3">
          <div
            class="card-header d-flex flex-column flex-sm-row align-items-stretch bg-theme rounded-0 border-bottom align-items-sm-center p-0"
          >
            <div v-if="themeImg" class="bg-light p-3 align-self-stretch d-flex align-items-center">
              <img :src="themeImg" class="me-3" style="max-height: 50px; max-width: 100%" />
            </div>
            <div class="d-flex flex-grow-1 align-items-center justify-content-between p-3">
              <h3 class="mb-0">{{ title }}</h3>
            </div>
          </div>
          <div class="card-body h-100 p-3 p-md-5">
            <template v-if="slide.data">
              <header v-if="slide.data.title" class="h1 text-center">{{ slide.data.title[$i18n.locale] }}</header>
              <div v-if="slide.data.description" class="lead" v-html="slide.data.description[$i18n.locale]" />
            </template>


            <template v-if="contestsOpened">
              <AVSplitHelper
                :key="contests[contestIndex].reference"
                :contest="formatContest(contests[contestIndex])"
                :contestSelection="contestSelection(contests[contestIndex])"
                :partial-results="partialResults"
                :weight="1"
                :show-submission-helper="true"
              />

              <div class="d-flex mt-3 gap-3 flex-column-reverse flex-md-row">
                <button v-if="contestIndex > 0" class="btn btn-lg btn-ballot-outline col-12 col-md-auto"
                  @click="goBack" v-text="$t('js.election_client.actions.back')" />
                <button v-if="!isLastContest" class="btn btn-lg btn-theme ms-auto col-12 col-md-auto"
                  @click="goForward" v-text="$t('js.election_client.actions.continue')" />
              </div>
            </template>

            <template v-else>
              <ContestHeader
                v-for="contest in orderedContests"
                :contest="formatContest(contest)"
                :show-options="true"
                :locale="$i18n.locale"
              />
              <hr>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>

  <AVModal
    :modalActive="modalActive"
    :ariaLabelledBy="$t('js.backend.ballot_preview.interactive_preview')"
    @closeModal="toggleModal"
  >
    <button class="btn btn-outline-dark float-end btn-sm" @click="toggleModal">✕</button>
    <h4 class="mb-4">{{ $t("js.backend.ballot_preview.modal.title") }}</h4>
    <p><strong>{{ $t("js.backend.ballot_preview.modal.subtitle") }}</strong></p>
    <div class="alert alert-warning my-4">
      <i class="fas fa-exclamation-triangle me-1"></i>
      {{ $t("js.backend.ballot_preview.modal.alert") }}
    </div>
    <p><strong>{{ $t("js.backend.ballot_preview.modal.what_to_check") }}</strong></p>
    <p>{{ $t("js.backend.ballot_preview.modal.examples") }}</p>
    <ul>
      <li>{{ $t("js.backend.ballot_preview.modal.example_1") }}</li>
      <li>{{ $t("js.backend.ballot_preview.modal.example_2") }}</li>
      <li>{{ $t("js.backend.ballot_preview.modal.example_3") }}</li>
      <li>{{ $t("js.backend.ballot_preview.modal.example_4") }}</li>
      <li>{{ $t("js.backend.ballot_preview.modal.example_5") }}</li>
    </ul>
  </AVModal>
</template>

<script>
import ContestHeader from "../shared/ContestHeader.vue";
import { formatContest, orderContests, flattenOptions } from "../../entrypoints/shared/contest_utilities";

export default {
  name: "BallotPreview",
  components: { ContestHeader },
  props: {
    contests: Array,
    contestPositions: Object,
    slide: Object,
    themeImg: String,
    backgroundStyle: String,
    height: Number,
    votingRoundTitles: Object,
    initialLocale: String,
    availableLocales: Array
  },
  data() {
    return {
      activeSplitState: "ballot",
      contestIndex: 0,
      isLastContest: false,
      confirming: false,
      contestsOpened: false,
      modalActive: false,
      partialResults: null,
      selectedLocale: this.initialLocale,
      electionTitle: ''
    }
  },
  computed: {
    orderedContests() {
      return orderContests(this.contests, this.contestPositions);
    },
    title() {
      return this.votingRoundTitles[this.$i18n.locale]
    },
  },
  methods: {
    formatContest,
    orderContests,
    flattenOptions,
    contestSelection(contest) {
      return {
        reference: contest.reference,
        piles: [],
      }
    },
    goBack() {
      this.contestIndex--;
      this.confirming = false;
      this.isLastContest = this.contestIndex + 1 == this.contests.length;
    },
    goForward() {
      this.contestIndex++;
      this.isLastContest = this.contestIndex + 1 == this.contests.length;
    },
    toggleContestsOpened() {
      this.contestsOpened = !this.contestsOpened;
    },
    toggleModal() {
      this.modalActive = !this.modalActive;
    },
    setSelectedLocale(selected){
      this.selectedLocale = selected
      this.$i18n.locale = selected
    }
  },
  created() {
    this.$i18n.locale = this.initialLocale
    this.isLastContest = this.contests.length == 1 ? true : false;

    if (this.slide.hand_raise) {
      let initial = {
        blank: {
          reference: "blank",
          results: { count: 0, percentage: 0 }
        },
      };

      this.flattenOptions(this.contests[0].options).forEach(option => {
        initial[option.reference] = {
          reference: option.reference,
          results: { count: 0, percentage: 0 }
        }
      });

      this.partialResults = initial;
    }
  },
}
</script>
